import { useGlobal } from '@/context/GlobalContext';
import { usePageContent } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO, { serviceSchema } from '@/components/SEO';
import { 
  Calendar, Cog, Eye, Cpu, Wrench, ClipboardCheck, FlaskConical, Truck,
  CheckCircle, ChevronRight, ArrowRight, Users, Building2
} from 'lucide-react';

const EngineeringPage = () => {
  const { settings } = useGlobal();
  const { content, loading: contentLoading } = usePageContent('engineering');

  // Get content with defaults
  const hero = content.hero || {};
  const chipsetExpertiseCMS = content.chipset_expertise || {};
  const servicesCMS = content.services || {};
  const processCMS = content.process || {};
  const whoWeWorkWithCMS = content.who_we_work_with || {};
  const cta = content.cta || {};

  // Default values
  const defaults = {
    hero: {
      badge: "OEM / ODM",
      headline: "Engineering & OEM/ODM Services",
      description: "From concept to certified product — we handle the engineering complexity so you can focus on your market.",
      background_image: "https://static.prod-images.emergentagent.com/jobs/a9e4d36a-e5ff-43e1-ab5f-86fa29a8738a/images/e2bbcd2de44b69cf2a3f6d037742da4181831c1667259c04bda26f038d9c171f.png"
    },
    chipset_expertise: {
      badge: "OUR CORE STRENGTH",
      title: "Automotive-Grade Chipset Expertise",
      paragraphs: [
        "We don't manufacture chips — but we know how to work with them. Our engineering team has deep experience with automotive-grade SoCs and image sensors, enabling us to design products that perform reliably in demanding vehicle environments.",
        "This expertise is the foundation of our OEM/ODM capability. We speak the technical language of imaging — optics, ISP tuning, thermal management, EMC compliance — so you don't have to."
      ],
      categories: [
        { category: 'Automotive SoCs', items: ['Ambarella', 'Novatek', 'Eeasy', 'Allwinner'] },
        { category: 'Image Sensors', items: ['Sony STARVIS', 'OmniVision', 'Galaxycore', 'Weiguang'] },
        { category: 'Capabilities', items: ['HDR/WDR Optimization', 'Low-Light Tuning', 'ADAS Integration', 'AI/ML Processing'] },
        { category: 'Standards', items: ['IATF 16949', 'ISO 9001', 'E-Mark', 'ECE R118'] }
      ]
    },
    services: {
      title: "What We Handle",
      description: "End-to-end engineering support from initial concept through production launch.",
      items: [
        { icon: 'ClipboardCheck', title: 'Requirements Analysis', description: 'Comprehensive assessment of your imaging and system requirements.' },
        { icon: 'Cog', title: 'System Architecture', description: 'Complete system design including component selection and integration.' },
        { icon: 'Eye', title: 'Optical Design', description: 'Lens selection and optical path optimization for your use case.' },
        { icon: 'Cpu', title: 'ISP & IQ Tuning', description: 'Image Signal Processor configuration for optimal quality.' },
        { icon: 'FlaskConical', title: 'Prototyping', description: 'Functional prototypes for validation and testing.' },
        { icon: 'ClipboardCheck', title: 'Certification Support', description: 'Guidance through CE, FCC, RoHS, E-Mark certification.' },
        { icon: 'Wrench', title: 'Manufacturing Coordination', description: 'Partner selection and production management.' },
        { icon: 'Truck', title: 'Logistics & Delivery', description: 'Quality assurance and shipping coordination.' }
      ]
    },
    process: {
      title: "Our Process",
      description: "A structured methodology refined over hundreds of projects — ensuring every engagement delivers on performance, timeline, and budget.",
      phases: [
        { phase: 'Discovery', desc: 'Understanding your requirements, constraints, and market goals.' },
        { phase: 'Design', desc: 'System architecture, component selection, and specifications.' },
        { phase: 'Prototype', desc: 'Functional samples for validation and approval.' },
        { phase: 'Certification', desc: 'Testing and documentation for required standards.' },
        { phase: 'Production', desc: 'Manufacturing coordination with our partner network.' }
      ],
      engagement: {
        title: "Typical Engagement",
        items: [
          { label: 'Initial consultation', value: 'Free' },
          { label: 'Prototype timeline', value: '4-8 weeks' },
          { label: 'Production MOQ', value: 'Project dependent' },
          { label: 'Certification support', value: 'CE, FCC, RoHS, E-Mark' }
        ]
      }
    },
    who_we_work_with: {
      title: "Who We Work With",
      description: "We partner with brands, distributors, and integrators who need imaging expertise.",
      types: [
        { icon: 'Building2', title: 'Brands & OEMs', description: 'You have the brand and market — we provide the product and engineering.', examples: 'Automotive accessory brands, vehicle manufacturers, fleet companies' },
        { icon: 'Users', title: 'Distributors', description: 'Ready-to-brand products or custom development for your portfolio.', examples: 'Regional distributors, online retailers, B2B wholesalers' },
        { icon: 'Cog', title: 'System Integrators', description: 'Components and modules for your larger system or project.', examples: 'Security integrators, fleet management providers, IoT developers' }
      ]
    },
    cta: {
      title: "Ready to Start Your Project?",
      description: "Schedule a call with our engineering team to discuss your imaging solution requirements. Initial consultation is always free."
    }
  };

  // Merge CMS content with defaults
  const h = { ...defaults.hero, ...hero };
  const ce = { ...defaults.chipset_expertise, ...chipsetExpertiseCMS };
  const srv = { ...defaults.services, ...servicesCMS };
  const proc = { ...defaults.process, ...processCMS };
  const wwww = { ...defaults.who_we_work_with, ...whoWeWorkWithCMS };
  const c = { ...defaults.cta, ...cta };

  const services = srv.items || defaults.services.items;
  const chipsetExpertise = ce.categories || defaults.chipset_expertise.categories;
  const process = proc.phases || defaults.process.phases;
  const whoWeWorkWith = wwww.types || defaults.who_we_work_with.types;

  // Icon mapping
  const iconMap = { ClipboardCheck, Cog, Eye, Cpu, FlaskConical, Wrench, Truck, Building2, Users };

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="engineering-page">
      <SEO 
        title="OEM/ODM Engineering Services - Custom Camera Development"
        description="Full-service OEM/ODM for imaging products. Automotive-grade chipset expertise, ISP tuning, optical design, prototyping, certification support (CE, FCC, RoHS, E-Mark). From concept to production."
        keywords="camera OEM service, ODM development, automotive chipset engineering, ISP tuning service, CE FCC certification, product design Taiwan, imaging prototype, Sony sensor integration, Sony STARVIS tuning, automotive vehicle camera design"
        canonicalUrl="https://www.trinity-tw.com/engineering"
        ogImage="https://static.prod-images.emergentagent.com/jobs/a9e4d36a-e5ff-43e1-ab5f-86fa29a8738a/images/e2bbcd2de44b69cf2a3f6d037742da4181831c1667259c04bda26f038d9c171f.png"
        structuredData={serviceSchema('OEM/ODM Engineering Services', 'Full-service OEM/ODM for imaging products with automotive-grade chipset expertise, ISP tuning, optical design, and certification support.')}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/30 via-[#1a1f2e]/50 to-[#1a1f2e]/60" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#1e2530] border border-[#3d4758] text-sm text-slate-300 mb-6">
            {h.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            {h.headline}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {h.description}
          </p>
        </div>
      </section>

      {/* Automotive Chipset Expertise */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#5b3589]/20 text-xs text-[#a78bfa] font-medium mb-4">
                {ce.badge}
              </div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
                {ce.title}
              </h2>
              {(ce.paragraphs || defaults.chipset_expertise.paragraphs).map((para, idx) => (
                <p key={idx} className="text-slate-400 mb-6">{para}</p>
              ))}
              <Link to="/contact">
                <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full">
                  Discuss Your Requirements
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {chipsetExpertise.map((group, idx) => (
                <div key={idx} className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-5">
                  <h4 className="text-white font-medium text-sm mb-3">{group.category}</h4>
                  <div className="space-y-2">
                    {group.items.map((item, iidx) => (
                      <div key={iidx} className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3 text-[#5b3589]" />
                        <span className="text-slate-400 text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {srv.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {srv.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const IconComponent = iconMap[service.icon] || ClipboardCheck;
              return (
                <div key={idx} className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6 hover:border-[#5b3589] transition-colors">
                  <IconComponent className="w-8 h-8 text-[#a78bfa] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2 font-['Montserrat']">
                    {service.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
                {proc.title}
              </h2>
              <p className="text-slate-400 mb-8">
                {proc.description}
              </p>
              
              <div className="space-y-6">
                {process.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-8 h-8 bg-[#5b3589] text-white rounded-full flex items-center justify-center shrink-0 font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-white font-['Montserrat']">{item.phase}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
              <h3 className="text-xl font-bold text-white mb-6 font-['Montserrat']">
                {(proc.engagement || defaults.process.engagement).title}
              </h3>
              <div className="space-y-4">
                {((proc.engagement || defaults.process.engagement).items || []).map((item, idx) => (
                  <div key={idx} className={`flex justify-between items-center py-3 ${idx < ((proc.engagement || defaults.process.engagement).items || []).length - 1 ? 'border-b border-[#3d4758]' : ''}`}>
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-white">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {wwww.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {wwww.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whoWeWorkWith.map((type, idx) => {
              const IconComponent = iconMap[type.icon] || Building2;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                  <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-[#8b5cf6]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 font-['Montserrat']">
                    {type.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">{type.description}</p>
                  <p className="text-slate-500 text-xs">{type.examples}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
            {c.title}
          </h2>
          <p className="text-slate-400 mb-8">
            {c.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Call
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Send Project Brief
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngineeringPage;
